import { useState } from 'react'

import {
  isPix,
  isValidTransferTypes,
  isWire,
} from '@services/type-guards/BankAccount'
import {
  usePixBankAccountLazyQuery,
  useWireBankAccountLazyQuery,
} from '@shared/graphql'

import type { BankAccount, Currency, TransferTypes } from '@shared/graphql'

/**
 * Custom hook to manage supported currencies from a bank account.
 *
 * Updates the supported currencies based on the bank account type.
 * - If the account type is provided:
 * - 1. Fetches the supported currencies via API.
 * - 2. The supported fiat currencies will be passed to the itemsFilter for the currency dropdown list.
 *
 * - If the account type is not checked or recognized:
 * - 1. An empty list will be passed to the itemsFilter for the currency dropdown list.
 * - 2. Default options are (USD, EUR).
 *
 */
export const useSupportedCurrencies = () => {
  const [getWireBankAccount] = useWireBankAccountLazyQuery()
  const [getPixBankAccount] = usePixBankAccountLazyQuery()
  const [supportedFiatCurrencies, setSupportedFiatCurrencies] = useState<
    Currency[]
  >([])

  const updateSupportedFiatCurrencies = (transferTypes: TransferTypes[]) => {
    if (transferTypes.length > 0) {
      const allCurrencies = transferTypes.flatMap((type) => type.currencies)
      setSupportedFiatCurrencies([...new Set(allCurrencies)])
    }
  }

  const updateWireSupportedCurrencies = (accountId: string) => {
    void getWireBankAccount({
      variables: { accountId },
      fetchPolicy: 'no-cache',
      onCompleted(data) {
        const transferTypes = data.wireBankAccount.transferTypes

        if (transferTypes != null) {
          const types = transferTypes.filter(isValidTransferTypes)
          updateSupportedFiatCurrencies(types)
        }
      },
    })
  }

  const updatePixSupportedCurrencies = (accountId: string) => {
    void getPixBankAccount({
      variables: { accountId },
      fetchPolicy: 'no-cache',
      onCompleted(data) {
        const transferTypes = data.pixBankAccount.transferTypes

        if (transferTypes != null) {
          const types = transferTypes.filter(isValidTransferTypes)
          updateSupportedFiatCurrencies(types)
        }
      },
    })
  }

  const updateSupportedCurrenciesFromAccount = (account: BankAccount) => {
    if (isWire(account)) {
      updateWireSupportedCurrencies(account.id)
    }

    if (isPix(account)) {
      updatePixSupportedCurrencies(account.id)
    }

    setSupportedFiatCurrencies([])
  }

  return { updateSupportedCurrenciesFromAccount, supportedFiatCurrencies }
}
